import { useMemo } from "react";
import { ChevronRight } from "lucide-react";
import { buildLeagueSnapshot, buildLedger, buildManagerProfiles } from "@averagejoesff/shared";
import type { LeagueHistory, PlayoffOdds } from "@averagejoesff/shared";
import { AppLink } from "../../components/AppLink";
import { ResourceStatus } from "../../components/ResourceStatus";
import { StateMessage } from "../../components/StateMessage";
import { useLeagueHistory } from "../../lib/leagueHistory";
import { playoffOddsFor } from "../../lib/playoffOdds";
import { useViewer } from "../../lib/viewer";
import { indexPeople } from "../managers/people";
import { LeagueGlance } from "./LeagueGlance";
import { LeagueSlate } from "./LeagueSlate";
import { ManagerPicker } from "./ManagerPicker";
import { MyWeek } from "./MyWeek";
import { StandingsTable } from "./StandingsTable";
import { useCurrentReport } from "./useCurrentReport";

/**
 * The landing page: the visitor's own team and game when they've said who they
 * are, then the week's slate and the standings for everyone. Built entirely
 * from the published league history.
 */
export function HomeView() {
  const history = useLeagueHistory();

  return (
    <section className="feature" aria-labelledby="home-heading">
      <div className="feature__intro">
        <h2 id="home-heading" className="feature__title">
          Home
        </h2>
        <p className="feature__description">Your team, this week's games and the standings.</p>
      </div>

      <ResourceStatus resource={history} loading="Loading the league…" error="Could not load the league" />
      {history.status === "ready" &&
        (history.data === null ? (
          <StateMessage variant="empty" title="No league history yet" detail="Check back once the first results are in." />
        ) : (
          <HomeBody history={history.data} />
        ))}
    </section>
  );
}

function HomeBody({ history }: { history: LeagueHistory }) {
  const [viewer, setViewer] = useViewer();
  const ledger = useMemo(() => buildLedger(history), [history]);
  const snapshot = useMemo(() => buildLeagueSnapshot(history), [history]);
  const profiles = useMemo(() => buildManagerProfiles(history), [history]);
  const people = useMemo(() => indexPeople(history), [history]);
  // Null once the season is over or when the schedule hasn't been recorded; the table then has no odds columns.
  const odds = useMemo(() => playoffOddsFor(history), [history]);
  // The published report for this week, when there is one: MyWeek shows its card and the slate links to it.
  const report = useCurrentReport(snapshot?.season ?? null, snapshot?.week ?? null);
  // A remembered manager the history no longer lists is treated as nobody.
  const myId = viewer?.kind === "manager" ? viewer.personId : null;
  const me = profiles.find((p) => p.personId === myId) ?? null;

  if (!snapshot) {
    return <StateMessage variant="empty" title="No seasons yet" detail="Check back once the season starts." />;
  }
  // Consolation games are listed apart, so the main slate reads as the week's real games.
  const mainGames = snapshot.slate.filter((g) => g.gameType !== "consolation");
  const consolationGames = snapshot.slate.filter((g) => g.gameType === "consolation");

  return (
    <>
      {me ? (
        <MyWeek
          me={me}
          profiles={profiles}
          snapshot={snapshot}
          ledger={ledger}
          people={people}
          report={report}
          onForget={() => setViewer(null)}
        />
      ) : viewer?.kind === "spectator" ? (
        <LeagueGlance snapshot={snapshot} profiles={profiles} people={people} onReset={() => setViewer(null)} />
      ) : (
        <ManagerPicker
          profiles={profiles}
          onPick={(personId) => setViewer({ kind: "manager", personId })}
          onSpectate={() => setViewer({ kind: "spectator" })}
        />
      )}

      {/* On a phone the slate is swapped for one link to the Matchups page; a spectator already has that link above. */}
      {snapshot.slate.length > 0 && viewer?.kind !== "spectator" && (
        <AppLink href="/matchups" className="home-slate-more">
          View other matchups
          <ChevronRight size={18} aria-hidden="true" />
        </AppLink>
      )}
      <div className="home-slate-section">
        {mainGames.length > 0 && (
          <>
            <h3 className="feature__subhead">{`Week ${snapshot.week} games`}</h3>
            <LeagueSlate snapshot={snapshot} games={mainGames} people={people} myId={me?.personId ?? null} report={report} />
          </>
        )}
        {consolationGames.length > 0 && (
          <>
            <h3 className="feature__subhead">{`Week ${snapshot.week} consolation games`}</h3>
            <LeagueSlate snapshot={snapshot} games={consolationGames} people={people} myId={me?.personId ?? null} report={report} />
          </>
        )}
      </div>

      <h3 className="feature__subhead">{snapshot.isFinished ? `${snapshot.season} final standings` : `${snapshot.season} standings`}</h3>
      <StandingsTable
        standings={snapshot.standings}
        people={people}
        myId={me?.personId ?? null}
        caption={`${snapshot.season} standings`}
        odds={odds}
        playoffTeams={snapshot.playoffTeams}
      />
      {odds && <OddsNote odds={odds} />}
    </>
  );
}

/** What the odds are and how far to trust them, tucked away until someone wants it. */
function OddsNote({ odds }: { odds: PlayoffOdds }) {
  return (
    <details className="standings__about">
      <summary>How are the odds worked out?</summary>
      <p>
        {odds.weeksRemaining === 0
          ? "The regular season is over, so the playoff field is set."
          : `They come from ${odds.simulations.toLocaleString()} simulated finishes to the ${odds.weeksRemaining === 1 ? "last regular-season week" : `last ${odds.weeksRemaining} regular-season weeks`}, using the real schedule.`}{" "}
        The top {odds.playoffTeams} by record, then points scored, make the playoffs, so a team that scores a lot can have better odds
        than one just above it in the standings. Weekly scores swing by about 30 points, so expect these to move a lot from week to week,
        and don't read "&lt;1%" as impossible.
      </p>
    </details>
  );
}
